import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ListDropdown from '@alltrails/shared/components/ListDropdown';
import ChevDown12 from '@alltrails/shared/components/ChevDown12';
import PrimaryButtonLarge from '@alltrails/shared/components/PrimaryButtonLarge';
import SlidingTabs from 'components/shared/SlidingTabs';
import useToggle from 'hooks/useToggle';
import * as styles from './styles/styles.module.scss';
import navScrollTo from './navScrollTo';
import { NavItem } from './types/NavItem';

type ScrollerNavProps = {
  items: NavItem[];
  title: string;
  selectedTabIndex: number;
  onTabIndexChange: (idx: number) => void;
  // eslint-disable-next-line react/require-default-props
  ctaInfo?: {
    title: string;
    onClick?: () => void;
  };
};

const ScrollerNav = ({ items, title, selectedTabIndex, onTabIndexChange, ctaInfo }: ScrollerNavProps): JSX.Element => {
  const location = useLocation();
  const shouldCollapseNav = useMediaQuery(`(max-width:1024px)`);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const tabOptions = items.map(item => ({
    label: item.title,
    href: '/'
  }));

  const handleNavChange = useCallback(
    (idx: number) => {
      navScrollTo(items[idx].id);
      onTabIndexChange(idx);
      setShowDropdown(false);
    },
    [items, setShowDropdown, onTabIndexChange]
  );

  const handleTabChange = (e: React.ChangeEvent<HTMLElement>, idx: number) => {
    handleNavChange(idx);
  };

  useEffect(() => {
    const sectionId = location?.hash.substring(1);
    if (sectionId) {
      const sectionIdx = items.findIndex(item => item.id === sectionId);
      sectionIdx !== -1 && handleNavChange(sectionIdx);
    }
  }, []);

  return (
    <div className={styles.navContainer}>
      <div className={styles.navBar}>
        {shouldCollapseNav ? (
          <ListDropdown
            anchorElement={
              <button type="button" className={styles.navAnchor} onClick={() => setShowDropdown(showDropdown)}>
                <span className={styles.navTitle}>{title}</span>
                <ChevDown12 isRotated={showDropdown} />
              </button>
            }
            links={tabOptions}
            handleLinkClick={handleNavChange}
            isDropdownOpen={showDropdown}
            setIsDropdownOpen={(isOpen: boolean) => setShowDropdown(isOpen)}
          />
        ) : (
          <>
            <span className={styles.navTitle}>{title}</span>
            <SlidingTabs
              slidingTabsClasses={{
                root: styles.navRoot,
                flexContainer: styles.navOptionsContainer,
                scroller: styles.navScroller,
                indicator: styles.navIndicator
              }}
              tabClasses={{ root: styles.navItem, wrapper: styles.navItemWrapper, selected: styles.selectedNavItem }}
              tabs={tabOptions}
              onChange={handleTabChange}
              activeIndex={selectedTabIndex}
              scrollButtons="off"
            />
          </>
        )}
        {ctaInfo && (
          <PrimaryButtonLarge name={ctaInfo.title} className={styles.ctaButton} onClick={ctaInfo.onClick}>
            {ctaInfo.title}
          </PrimaryButtonLarge>
        )}
      </div>
    </div>
  );
};

export { ScrollerNav as default, ScrollerNavProps };
